import { useEffect, useRef } from "react";
import { getTenant } from "@/TENANT_CONFIGS";
import { useSessionStorage } from "./useSessionStorage";

export const getSavedTplSelectionKey = (tenant, module, storeKey) =>
  `savedTplSelection_${tenant}_${module}${storeKey ? `_${storeKey}` : ""}`;

/**
 * Persist a template/content-type selection in sessionStorage, keyed per
 * tenant + module (+ optional storeKey) — same key convention as
 * useSavedFilterConfig.
 *
 * Stores only kp_content_type ids, not tpl objects. Restores once, after the
 * options have loaded; ids that no longer exist in this context's options
 * (deleted template / whitelist change) are dropped. An EMPTY saved selection
 * is a valid state and restores as empty — only "nothing saved yet" leaves
 * the module's own default selection untouched.
 */
export const useSavedTplSelection = ({
  module,
  storeKey,
  optionIds = [], // all valid kp_content_type ids in this context
  selectedIds = [], // current selection as ids
  restoreSelection, // (ids) => void — apply a restored selection
  enabled = true,
}) => {
  const key = getSavedTplSelectionKey(getTenant(), module, storeKey);
  const [saved, setSaved] = useSessionStorage(key, null);

  const hasRestoredRef = useRef(false);
  const skipWriteRef = useRef(false);

  // Restore once, as soon as options are available
  const optionIdsJson = JSON.stringify(optionIds);
  useEffect(() => {
    if (!enabled || hasRestoredRef.current || !optionIds.length) return;
    hasRestoredRef.current = true;
    if (!Array.isArray(saved)) return; // nothing saved → keep module default
    const validIds = new Set(optionIds);
    // The write effect below runs right after this one with this render's
    // PRE-restore selection — skip that one write so it can't overwrite the
    // saved value we just restored.
    skipWriteRef.current = true;
    restoreSelection(saved.filter((id) => validIds.has(id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, optionIdsJson, saved, restoreSelection]);

  // Write-through on selection change (only once restore has had its chance)
  const selectedIdsJson = JSON.stringify(selectedIds);
  useEffect(() => {
    if (!enabled || !hasRestoredRef.current) return;
    if (skipWriteRef.current) {
      skipWriteRef.current = false;
      return;
    }
    setSaved(JSON.parse(selectedIdsJson));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, selectedIdsJson]);
};
